import _ from 'lodash';
import { LockIcon, SettingsIcon } from 'lucide-react';
import { makeAutoObservable, runInAction } from 'mobx';
import { observer } from 'mobx-react-lite';
import { useEffect } from 'react';
import { UserResponse } from '../../../server/response/responseScheme';
import { apiClient } from '../../clientUtils/apiClient';
import { useMobxInstance } from '../../clientUtils/mobxUtil';
import firebaseStore from '../../stores/firebaseStore';
import { twMerge } from 'tailwind-merge';

class ViewStore {
    currentUser: UserResponse | null = null;
    isLoading = false;

    constructor() {
        makeAutoObservable(this);
    }

    async fetch() {
        this.isLoading = true;

        const isLoggedIn = await firebaseStore.isLoggedIn();

        if (isLoggedIn) {
            try {
                const currentUser = await apiClient.user.member.getCurrentUser.query();
                runInAction(() => {
                    this.currentUser = currentUser;
                    this.isLoading = false;
                });
            } catch (e) {
                // 토큰이 유효하지 않은 경우임
                runInAction(() => {
                    this.currentUser = null;
                    this.isLoading = false;
                });
            }
        } else {
            runInAction(() => {
                this.currentUser = null;
                this.isLoading = false;
            });
        }
    }
}

const ListeningUserProfile = observer(
    ({ className, isShowName }: { className?: string; isShowName: boolean }) => {
        const viewStore = useMobxInstance(ViewStore);

        useEffect(() => {
            viewStore.fetch();
        }, [firebaseStore.me]);

        const shouldShowLoading = !firebaseStore.isInitialized || viewStore.isLoading;

        return (
            <div className={twMerge('flex h-10 items-center', className)}>
                {shouldShowLoading && (
                    <div className="flex h-10 w-full items-center justify-center">
                        <div className="loading loading-ring"></div>
                    </div>
                )}
                {!shouldShowLoading && viewStore.currentUser && (
                    <div className="flex flex-row items-center gap-3">
                        {viewStore.currentUser.picture ? (
                            <div className="avatar shrink-0">
                                <div className="w-9 rounded-full">
                                    <img
                                        className="size-full object-cover"
                                        src={viewStore.currentUser.picture}
                                    />
                                </div>
                            </div>
                        ) : (
                            <div className="avatar avatar-placeholder shrink-0">
                                <div className="bg-base-content/10 w-9 rounded-full">
                                    <span className="text-base">
                                        {_.first(viewStore.currentUser.name) || ''}
                                    </span>
                                </div>
                            </div>
                        )}
                        {isShowName && (
                            <div className="flex grow flex-row items-center justify-between gap-4 truncate">
                                <div className="flex grow flex-col justify-center truncate">
                                    <div className="h-5 truncate text-sm font-semibold">
                                        {viewStore.currentUser.name}
                                    </div>
                                    <div className="text-base-content/60 h-4.5 truncate text-xs">
                                        {viewStore.currentUser.email}
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                )}
                {!shouldShowLoading && !viewStore.currentUser && (
                    <div className="flex flex-row items-center gap-3">
                        <div className="avatar avatar-placeholder">
                            <div className="bg-base-content/10 w-9 overflow-hidden rounded-full">
                                <LockIcon className="text-base-content/15 size-4" />
                            </div>
                        </div>
                        {isShowName && (
                            <div className="flex flex-col justify-center">
                                <div className="text-sm">로그인하기</div>
                            </div>
                        )}
                    </div>
                )}
            </div>
        );
    },
);

export default ListeningUserProfile;
